import { v4 } from 'uuid';
import ReactDOM from 'react-dom';
import { createStore } from 'redux';
import { reducer } from './reducer';
import { EditorState } from './state';
import { buildingFormValid } from './util';
import { PayloadAction } from '@reduxjs/toolkit';
import { formService } from '../services/formService';
import React from 'react';
import { loadSchematic, setMode } from '../schematic/actions';
import { systemMetadataMap } from '../schematic/components/system-metadata';
import { getConnectionPointViewModel, getConnectionPointVms } from '../schematic/util/rendering-util';
import { AppModel, ConnectionModel, SchematicMode, SystemConnectionPointModel } from '../schematic/types';
import {DumbledoreEditor} from './components/DumbledoreEditor/DumbledoreEditor';

export interface RenderDumbledoreEditorResult {
  dispatch: (action: any) => void;
  getState: () => EditorState;
  exportModel: () => AppModel;
  load: (schematic: AppModel) => void;
}

export const renderDumbledoreEditor = (container: HTMLElement): RenderDumbledoreEditorResult => {

  const store = createStore(reducer);
  store.dispatch(setMode({ mode: SchematicMode.Edit }));

  const getState = (): EditorState => {
    return store.getState();
  };

  const dispatch = (action: PayloadAction<any>): void => {
    store.dispatch(action);
    ReactDOM.render(React.createElement(DumbledoreEditor, { getState, dispatch, exportModel }), container);
  };

  const load = (schematic: AppModel): void => {
    const action = loadSchematic({ schematic });
    dispatch(action);
  };

  const exportModel = (): AppModel => {
    const schematicState = store.getState().schematic;
    const systems = schematicState.systems;
    const connectionPoints = getConnectionPointVms(schematicState.points, schematicState.systems).map((vm): any => {
      const point: any = {
        left: vm.x,
        top: vm.y,
        type: vm.type,
        dotted: vm.id.dotted,
      };
      if (vm.id.type === 'FREE') {
        point.hasId = vm.id.id;
      } else {
        point.hasId = v4();
        point.connectionPointPlacement = vm.id.key;
        point.systemId = vm.id.systemId;
      }
      return point;
    });

    const removeNullsFromInfo = (data: any): any => {
      return Object.entries(data).reduce((acc: { [k: string]: any }, [key, val]) => {
        if (val !== null) {
          acc[key] = val;
        }
        return acc;
      }, {});
    };

    const connections: ConnectionModel[] = schematicState.connections.map((connection) => {
      const pointIds = [connection.from, connection.to].map((compositeId) => {
        if (compositeId.type === 'FREE') {
          return compositeId.id;
        }
        const point: SystemConnectionPointModel = connectionPoints.find(
          (p: any) => p.systemId === compositeId.systemId && p.connectionPointPlacement === compositeId.key
        );
        return point.hasId;
      });
      const pointVm = getConnectionPointViewModel(schematicState, connection.from);
      return {
        hasId: connection.id,
        connectionPointIds: pointIds,
        type: pointVm.type,
      };
    });
    const exportedSystems = systems.map((system) => {
      const meta = systemMetadataMap[system.type];
      const systemInfoWithoutNulls = removeNullsFromInfo(system.systemInfo);
      const systemWithoutNulls = { ...system, systemInfo: systemInfoWithoutNulls };
      return { ...systemWithoutNulls, configured: formService.formValid(meta.form, system.systemInfo) };
    });
    const valid = exportedSystems.every((system) => system.configured) && buildingFormValid(store.getState().buildingInfo);
    return {
      systems: exportedSystems,
      connectionPoints,
      connections,
      valid,
      buildingInfo: removeNullsFromInfo(store.getState().buildingInfo),
      modifiedTimestamp: Date.now(),
      draft: false,
    };
  };

  ReactDOM.render(React.createElement(DumbledoreEditor, { getState, dispatch, exportModel }), container);

  return {
    getState,
    dispatch,
    exportModel,
    load,
  };
};
